import type { ModuleSearchResult } from '@/lib/api/search'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

const MAX_RECENT_QUERIES = 10

interface SearchPreferencesState {
  /** Last selected module filter pills */
  selectedModuleTypes: string[]
  /** Last search query */
  lastQuery: string
  /** History of recent search queries (newest first, max 10) */
  recentQueries: string[]
  /** Cached search results (in-memory only, survives navigation but not page refresh) */
  cachedResults: ModuleSearchResult[]
  /** The scroll position of the results container */
  cachedScrollTop: number

  /** Persist the selected module filter pills */
  setSelectedModuleTypes: (types: string[]) => void
  /** Persist the last search query */
  setLastQuery: (query: string) => void
  /** Add a query to the recent history */
  addRecentQuery: (query: string) => void
  /** Remove a specific query from history */
  removeRecentQuery: (query: string) => void
  /** Clear all recent queries */
  clearRecentQueries: () => void
  /** Cache search results in memory */
  setCachedResults: (results: ModuleSearchResult[]) => void
  /** Cache scroll position */
  setCachedScrollTop: (scrollTop: number) => void
}

export const useSearchPreferencesStore = create<SearchPreferencesState>()(
  persist(
    (set, get) => ({
      selectedModuleTypes: [],
      lastQuery: '',
      recentQueries: [],
      cachedResults: [],
      cachedScrollTop: 0,

      setSelectedModuleTypes: types => set({ selectedModuleTypes: types }),

      setLastQuery: query => set({ lastQuery: query }),

      addRecentQuery: query => {
        const trimmed = query.trim()
        if (!trimmed) return

        const { recentQueries } = get()
        // Remove duplicate if it exists, then prepend. Case-insensitive:
        // "acme" and "Acme" are the same search, listed once (latest spelling).
        const key = trimmed.toLowerCase()
        const filtered = recentQueries.filter(q => q.toLowerCase() !== key)
        const updated = [trimmed, ...filtered].slice(0, MAX_RECENT_QUERIES)
        set({ recentQueries: updated })
      },

      removeRecentQuery: query => {
        set(state => ({
          recentQueries: state.recentQueries.filter(q => q !== query),
        }))
      },

      clearRecentQueries: () => set({ recentQueries: [] }),

      setCachedResults: results => set({ cachedResults: results }),

      setCachedScrollTop: scrollTop => set({ cachedScrollTop: scrollTop }),
    }),
    {
      name: 'scrambl-search-preferences',
      // Only persist preferences to localStorage, not cached results or scroll
      partialize: state => ({
        selectedModuleTypes: state.selectedModuleTypes,
        lastQuery: state.lastQuery,
        recentQueries: state.recentQueries,
      }),
    },
  ),
)
